import React, { useState, useEffect, useRef, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { useEventHandler, useGlobalId, useUpdateRef } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import ModalWrapper from './ModalWrapper'

/**
 * Create a React Modal component
 *
 * @param {import('./interfaces').ModalProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function modal (props, ref) {
  const { defaultOpen, renderInline, isClosable, containerSelector } = props

  const [open, setOpen] = useState(
    props.open !== undefined ? props.open : defaultOpen
  )
  const elementsId = useGlobalId('')
  const elementRef = useRef()

  useUpdateRef(ref, elementRef, { open, setOpen, close })
  useEffect(onOpenPropChanged, [props.open])
  useEventHandler('keyup', handleDocumentKeyup)
  const wrapperProps = {
    ...props,
    open,
    elementsId,
    elementRef,
    onCancel: handleCloseClick,
    onConfirm: handleConfirm
  }
  const container = containerSelector ? document.querySelector(containerSelector) : document.body
  // When rendering inline, element to find theme from is not needed,
  // otherwise need one that we can refer to
  return renderInline ? (
    <ModalWrapper {...wrapperProps} />
  ) : (
    <Fragment>
      <span className='zb-modal-placeholder' ref={elementRef} />
      {open &&
        createPortal(
          <ModalWrapper {...wrapperProps} />,
          container
        )}
    </Fragment>
  )

  function onOpenPropChanged () {
    if (props.open !== undefined) {
      setOpen(props.open)
    }
  }

  function handleDocumentKeyup (event) {
    // escape
    if (event.keyCode === 27 && open && isClosable) {
      close()
    }
  }

  function handleCloseClick (event) {
    event && event.preventDefault()
    close()
  }

  function close () {
    const { open, onCancel } = props

    if (open === undefined) {
      setOpen(false)
    }
    onCancel && onCancel()
  }

  function handleConfirm (event) {
    const { onConfirm } = props
    event.preventDefault()
    onConfirm && onConfirm()
  }
}

const Modal = React.forwardRef(modal)
Modal.displayName = 'Modal'

Modal.propTypes = {
  onOpen: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  confirm: PropTypes.string,
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  cancel: PropTypes.string,
  withBackdrop: PropTypes.bool,
  withSectioning: PropTypes.bool,
  withPadding: PropTypes.bool,
  isClosable: PropTypes.bool,
  className: PropTypes.string,
  renderHeader: PropTypes.func,
  renderFooter: PropTypes.func,
  containerSelector: PropTypes.string,
  renderInline: PropTypes.bool
}

Modal.defaultProps = {
  defaultOpen: false,
  withBackdrop: true,
  withSectioning: false,
  withPadding: true,
  isClosable: true,
  renderInline: false
}

export default Modal
